import React, {
  useState,
  useEffect,
  useRef,
  type ReactNode,
  useImperativeHandle,
  forwardRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link2 } from "lucide-react";

// Define the launchable piece type
export type LaunchablePiece = {
  id: string | number;
  position: number;
  content?: ReactNode;
  color?: string;
};

// Ref methods exposed to parent components
export type LaunchableAnimationRef = {
  addPiece: (pieceId: string | number) => void;
  addPieceWithContent: (pieceId: string | number, content: ReactNode) => void;
  resetLaunchable: () => void;
  removePiece: (pieceId: string | number) => void;
};

type LaunchableAnimationProps = {
  pieces: LaunchablePiece[];
  connectorContent?: ReactNode;
  className?: string;
  containerClassName?: string;
  emptyStateContent?: ReactNode;
  completionContent?: ReactNode;
  onComplete?: () => void;
  onPieceRemoved?: (pieceId: string | number) => void;
};

const LaunchableAnimation = forwardRef<
  LaunchableAnimationRef,
  LaunchableAnimationProps
>(
  (
    {
      pieces: initialPieces,
      connectorContent = <Link2 className="text-white w-6 h-6" />,
      className = "flex flex-col w-full mr-5",
      containerClassName = "p-1 relative w-full bg-primaryBg border border-zinc-800 dark:text-secondary rounded-md w-full",
      emptyStateContent = (
        <div className="absolute inset-0 flex items-center justify-center"></div>
      ),
      completionContent = <div></div>,
      onComplete,
      onPieceRemoved,
    },
    ref
  ) => {
    // Internal state for pieces with added status
    const [pieces, setPieces] = useState<
      (LaunchablePiece & { added: boolean; animationDirection?: string })[]
    >(initialPieces.map((piece) => ({ ...piece, added: false })));

    // Pieces that have been added to the launchable
    const [launchablePieces, setLaunchablePieces] = useState<
      (LaunchablePiece & { added: boolean; animationDirection?: string })[]
    >([]);

    // Reference to the launchable container
    const launchableContainerRef = useRef<HTMLDivElement>(null);

    // Expose methods to parent component via ref
    useImperativeHandle(ref, () => ({
      addPiece: (pieceId) => {
        addPiece(pieceId);
      },
      addPieceWithContent: (pieceId, content) => {
        addPieceWithContent(pieceId, content);
      },
      resetLaunchable: () => {
        resetLaunchable();
      },
      removePiece: (pieceId) => {
        removePiece(pieceId);
      },
    }));

    // Update launchable pieces when pieces state changes
    useEffect(() => {
      setLaunchablePieces(pieces.filter((piece) => piece.added));
    }, [pieces]);

    // Check if the launchable is complete (all pieces added)
    const isLaunchableComplete = pieces.every((piece) => piece.added);

    // Call onComplete when launchable becomes complete
    useEffect(() => {
      if (isLaunchableComplete && launchablePieces.length > 0 && onComplete) {
        onComplete();
      }
    }, [isLaunchableComplete, launchablePieces.length, onComplete]);

    // Add a specific piece
    const addPiece = (pieceId: string | number) => {
      // Find the piece to add
      const pieceToAdd = pieces.find((p) => p.id === pieceId);
      if (!pieceToAdd || pieceToAdd.added) return;

      // Create a new array of pieces
      const newPieces = [...pieces];
      const pieceIndex = newPieces.findIndex((p) => p.id === pieceId);

      if (pieceIndex === -1) return;

      // Get the pieces that are already added to determine animation direction
      const addedPieces = newPieces.filter((p) => p.added && p.id !== pieceId);
      const sortedAddedPieces = [...addedPieces].sort(
        (a, b) => a.position - b.position
      );

      // Determine animation direction
      let animationDirection: "from-left" | "from-right" | "from-above" =
        "from-left";

      if (sortedAddedPieces.length > 0) {
        const piecePosition = pieceToAdd.position;
        const lowestPosition = sortedAddedPieces[0].position;
        const highestPosition =
          sortedAddedPieces[sortedAddedPieces.length - 1].position;

        if (piecePosition < lowestPosition) {
          animationDirection = "from-left";
        } else if (piecePosition > highestPosition) {
          animationDirection = "from-right";
        } else {
          animationDirection = "from-above";
        }
      }

      // Update the piece with animation direction
      newPieces[pieceIndex] = {
        ...newPieces[pieceIndex],
        added: true,
        animationDirection,
      };

      setPieces(newPieces);
    };

    // Add a piece with custom content
    const addPieceWithContent = (
      pieceId: string | number,
      content: ReactNode
    ) => {
      console.log(pieceId);
      // Find the piece to add
      const pieceToAdd = pieces.find((p) => p.id === pieceId);
      if (!pieceToAdd) return;
      if (pieceToAdd.added) {
        removePiece(pieceId);
      }

      // Create a new array of pieces
      const newPieces = [...pieces];
      const pieceIndex = newPieces.findIndex((p) => p.id === pieceId);

      if (pieceIndex === -1) return;

      // Get the pieces that are already added to determine animation direction
      const addedPieces = newPieces.filter((p) => p.added && p.id !== pieceId);
      const sortedAddedPieces = [...addedPieces].sort(
        (a, b) => a.position - b.position
      );

      // Determine animation direction
      let animationDirection: "from-left" | "from-right" | "from-above" =
        "from-left";

      if (sortedAddedPieces.length > 0) {
        const piecePosition = pieceToAdd.position;
        const lowestPosition = sortedAddedPieces[0].position;
        const highestPosition =
          sortedAddedPieces[sortedAddedPieces.length - 1].position;

        if (piecePosition < lowestPosition) {
          animationDirection = "from-left";
        } else if (piecePosition > highestPosition) {
          animationDirection = "from-right";
        } else {
          animationDirection = "from-above";
        }
      }

      // Update the piece with animation direction and custom content
      newPieces[pieceIndex] = {
        ...newPieces[pieceIndex],
        added: true,
        animationDirection,
        content: content,
      };

      setPieces(newPieces);
    };

    // Remove a specific piece
    const removePiece = (pieceId: string | number) => {
      // Find the piece to remove
      const pieceToRemove = pieces.find((p) => p.id === pieceId);
      if (!pieceToRemove || !pieceToRemove.added) return;

      // Create a new array of pieces
      const newPieces = [...pieces];
      const pieceIndex = newPieces.findIndex((p) => p.id === pieceId);

      if (pieceIndex === -1) return;

      // Update the piece to mark it as not added
      newPieces[pieceIndex] = {
        ...newPieces[pieceIndex],
        added: false,
      };

      setPieces(newPieces);

      // Call the onPieceRemoved callback if provided
      if (onPieceRemoved) {
        onPieceRemoved(pieceId);
      }
    };

    // Reset the launchable
    const resetLaunchable = () => {
      setPieces(pieces.map((piece) => ({ ...piece, added: false })));
    };

    const renderElements = () => {
      // Get sorted puzzle pieces
      const sortedPieces = [...launchablePieces].sort(
        (a, b) => a.position - b.position
      );

      // Create an array to hold all elements (pieces and connectors)
      const elements: ReactNode[] = [];

      // Add pieces and connectors in the correct order
      for (let i = 0; i < sortedPieces.length; i++) {
        const piece = sortedPieces[i];

        // Add the piece
        elements.push(
          <motion.div
            key={`piece-${piece.id}`}
            className={`rounded-md shadow-md flex items-start justify-center min-w-[60px] min-h-[110px] max-h-[110px] max-w-[300px] p-3 overflow-auto`}
            initial={(() => {
              if (piece.animationDirection === "from-above") {
                return { y: -200, scale: 0, opacity: 0 };
              } else if (piece.animationDirection === "from-right") {
                return { x: 200, scale: 0, opacity: 0 };
              } else {
                return { x: -200, scale: 0, opacity: 0 };
              }
            })()}
            animate={{
              x: 0,
              y: 0,
              scale: 1,
              opacity: 1,
            }}
            exit={{
              scale: 0,
              opacity: 0,
              transition: { duration: 0.2 },
            }}
            transition={{
              type: "spring",
              damping: 20,
              stiffness: 200,
              duration: 0.5,
              layout: { duration: 0.4 },
            }}
            layout
          >
            {piece.content || (
              <span className="text-white font-bold text-xl">{piece.id}</span>
            )}
          </motion.div>
        );

        // If there's a next piece, add a connector
        if (i < sortedPieces.length - 1) {
          const nextPiece = sortedPieces[i + 1];
          const connectorId = `connector-${piece.id}-${nextPiece.id}`;

          elements.push(
            <motion.div
              key={connectorId}
              className={`flex items-center justify-center min-h-[80px] w-[40px] mx-5`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              exit={{
                scale: 0,
                opacity: 0,
                transition: { duration: 0.2 },
              }}
              transition={{
                type: "spring",
                damping: 20,
                stiffness: 200,
                duration: 0.5,
                delay: 0.1,
                layout: { duration: 0.4 },
              }}
              layout
            >
              {connectorContent}
            </motion.div>
          );
        }
      }

      return elements;
    };

    return (
      <div className={className}>
        {launchablePieces.length > 0 && (
          <div
            className={`${containerClassName} bg-[url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABsAAAAPCAQAAAC/mnxTAAAAIUlEQVR4AWPc7sywl4GBgUSScTsD6YBsbYPNkaOOHHUkALSqN8mc6mmTAAAAAElFTkSuQmCC")]`}
          >
            {/* Launchable pieces container */}
            <div
              ref={launchableContainerRef}
              className="relative flex items-center justify-center w-full"
            >
              {/* Render all elements in order */}
              <AnimatePresence mode="popLayout">
                <div className="flex items-center">{renderElements()}</div>
              </AnimatePresence>
            </div>

            {/* Empty state message */}
            {launchablePieces.length === 0 && emptyStateContent}
          </div>
        )}

        {/* Completion message */}
        {isLaunchableComplete &&
          launchablePieces.length > 0 &&
          completionContent}
      </div>
    );
  }
);

LaunchableAnimation.displayName = "LaunchableAnimation";

export default LaunchableAnimation;
